import { icon } from "./icons";
import { merchant, user } from "./data";

export type Route =
  | "dashboard"
  | "pay-ins"
  | "pay-outs"
  | "settlement"
  | "liquidity"
  | "reconciliation"
  | "statements"
  | "crypto"
  | "business"
  | "send"
  | "cards"
  | "payroll"
  | "beneficiaries"
  | "business-statements"
  | "promotions"
  | "team";

interface NavSection {
  key: string;
  label: string;
  icon: string;
  /** Where clicking the section header navigates. */
  route?: Route;
  /** Routes outside `subItems` that should still keep the section "open"
   *  (e.g. an overview page that doesn't appear as a sub-link). */
  alsoExpandsFor?: Route[];
  /** @deprecated Use `route` instead. */
  parentRoute?: Route;
  subItems?: { key: Route; label: string; count?: number }[];
}

const sections: NavSection[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: "dashboard",
    route: "dashboard",
  },
  {
    key: "customer-funds",
    label: "Customer Funds",
    icon: "customerFunds",
    route: "pay-ins",
    subItems: [
      { key: "pay-ins", label: "Pay-Ins", count: 128 },
      { key: "pay-outs", label: "Pay-Outs", count: 42 },
      { key: "settlement", label: "Settlement", count: 3 },
      { key: "liquidity", label: "Liquidity" },
      { key: "reconciliation", label: "Reconciliation" },
      { key: "statements", label: "Statements" },
    ],
  },
  { key: "crypto", label: "Crypto", icon: "crypto", route: "crypto" },
  {
    key: "business",
    label: "Business Account",
    icon: "business",
    route: "business",
    alsoExpandsFor: ["business"],
    subItems: [
      { key: "business", label: "Overview" },
      { key: "send", label: "Send" },
      { key: "cards", label: "Virtual Cards", count: 5 },
      { key: "payroll", label: "Payroll", count: 14 },
      { key: "beneficiaries", label: "Beneficiaries" },
      { key: "business-statements", label: "Statements" },
    ],
  },
];

const growthSections: NavSection[] = [
  {
    key: "promotions",
    label: "Promotions",
    icon: "promotions",
    route: "promotions",
  },
];

const configSections: NavSection[] = [
  {
    key: "settings",
    label: "Settings · Team",
    icon: "settings",
    route: "team",
  },
];

function navItemHtml(s: NavSection, currentRoute: Route): string {
  // A section with sub-items is "open" when the current route matches
  // any of its sub-items, OR any of its `alsoExpandsFor` routes.
  // Single-route sections are open when their route matches.
  const subRoutes = s.subItems?.map((i) => i.key) ?? [];
  const expandRoutes = s.alsoExpandsFor ?? [];
  const isInSubsection = subRoutes.includes(currentRoute);
  const isInExpansion = expandRoutes.includes(currentRoute);
  const isActive =
    s.route === currentRoute || isInSubsection || isInExpansion;
  const isOpen = isInSubsection || isInExpansion;

  const item = `<a class="nav-item ${isActive ? "is-active" : ""} ${isOpen ? "is-section-open" : ""}" data-route="${s.route ?? s.parentRoute ?? ""}" data-section="${s.key}">
    ${icon(s.icon as never, 16)}
    ${s.label}
  </a>`;

  const sub = s.subItems
    ? `<div class="nav-sub">
        ${s.subItems
          .map(
            (sub) =>
              `<a class="nav-sub-item ${sub.key === currentRoute ? "is-active" : ""}" data-route="${sub.key}">
                <span>${sub.label}</span>
                ${sub.count != null ? `<span class="nav-sub-count">${sub.count}</span>` : ""}
              </a>`,
          )
          .join("")}
      </div>`
    : "";

  return item + sub;
}

export interface LayoutHandles {
  setRoute(route: Route): void;
  canvasEl(): HTMLElement;
  navigate(route: Route): void;
}

export function mountLayout(
  root: HTMLElement,
  initialRoute: Route,
  onNavigate: (r: Route) => void,
): LayoutHandles {
  let currentRoute = initialRoute;

  function navHtml(): string {
    return `
      <div class="nav-group">
        <div class="nav-group-label">Operations</div>
        ${sections.map((s) => navItemHtml(s, currentRoute)).join("")}
      </div>
      <div class="nav-group">
        <div class="nav-group-label">Growth</div>
        ${growthSections.map((s) => navItemHtml(s, currentRoute)).join("")}
      </div>
      <div class="nav-group">
        <div class="nav-group-label">Configuration</div>
        ${configSections.map((s) => navItemHtml(s, currentRoute)).join("")}
      </div>
    `;
  }

  function render() {
    root.innerHTML = `
      <div class="app">
        <aside class="sidebar">
          <div class="brand">
            <div class="brand-mark"></div>
            <div>
              <div class="brand-name">Ivory Lion</div>
              <span class="brand-sub">Merchant Portal</span>
            </div>
          </div>

          <button class="merchant-pick" type="button" aria-label="Switch merchant">
            <div class="merchant-seal">${merchant.name
              .split(" ")
              .map((p) => p[0])
              .slice(0, 2)
              .join("")}</div>
            <div class="merchant-meta">
              <strong>${merchant.name}</strong>
              <span>${merchant.id} · ${merchant.status}</span>
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M8 9l4-4 4 4M8 15l4 4 4-4"/></svg>
          </button>

          <nav class="nav" aria-label="Primary navigation">
            ${navHtml()}
          </nav>

          <div class="sidebar-foot">
            <div class="sidebar-foot-avatar">${user.initials}</div>
            <div class="sidebar-foot-meta">
              <strong>${user.name}</strong>
              <span>${user.role}</span>
            </div>
            ${icon("arrowRight", 14)}
          </div>
        </aside>

        <main class="main">
          <header class="topbar">
            <div class="topbar-search">
              ${icon("search", 14)}
              <input type="search" placeholder="Search transactions, customers, beneficiaries…" aria-label="Search" />
              <span class="topbar-search-kbd">⌘K</span>
            </div>
            <div class="topbar-actions">
              <span class="env-chip">Production · ${merchant.reportingCurrency}</span>
              <span class="topbar-divider"></span>
              <button class="icon-btn theme-toggle" id="theme-toggle" aria-label="Toggle theme">
                <span class="sun">${icon("sun", 16)}</span>
                <span class="moon">${icon("moon", 16)}</span>
              </button>
              <button class="icon-btn" aria-label="Help">${icon("help", 16)}</button>
              <button class="icon-btn" aria-label="Notifications">
                ${icon("bell", 16)}
                <span class="icon-btn-dot"></span>
              </button>
            </div>
          </header>

          <div class="canvas" id="canvas"></div>
        </main>
      </div>
    `;

    // Wire nav clicks
    root.querySelectorAll<HTMLElement>("[data-route]").forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        const route = el.getAttribute("data-route") as Route;
        if (route && route !== currentRoute) onNavigate(route);
      });
    });

    // Theme toggle
    const toggle = root.querySelector<HTMLElement>("#theme-toggle");
    toggle?.addEventListener("click", () => {
      const next =
        document.documentElement.getAttribute("data-theme") === "dark"
          ? "light"
          : "dark";
      document.documentElement.setAttribute("data-theme", next);
      try {
        localStorage.setItem("il-theme", next);
      } catch {}
    });
  }

  function refreshNav() {
    const nav = root.querySelector<HTMLElement>(".nav");
    if (!nav) return;
    nav.innerHTML = navHtml();
    nav.querySelectorAll<HTMLElement>("[data-route]").forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        const route = el.getAttribute("data-route") as Route;
        if (route && route !== currentRoute) onNavigate(route);
      });
    });
  }

  render();

  return {
    setRoute(route) {
      currentRoute = route;
      refreshNav();
    },
    canvasEl() {
      const el = root.querySelector<HTMLElement>("#canvas");
      if (!el) throw new Error("canvas not mounted");
      return el;
    },
    navigate(r) {
      onNavigate(r);
    },
  };
}
